<template>
  <v-app>
    <v-main class="main-layout">
      <v-container class="page-container" fluid>
        <router-view />
      </v-container>
    </v-main>

    <!-- Spinner global -->
    <LoadingSpinner v-if="globalLoading" :message="loadingMessage" />

    <!-- Snackbar global com acessibilidade -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      location="top"
      elevation="6"
      rounded="lg"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      {{ snackbar.message }}
      <template #actions>
        <v-btn
          variant="text"
          aria-label="Fechar notificação"
          @click="snackbar.show = false"
        >
          Fechar
        </v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script setup>
import { ref, provide } from 'vue'
import LoadingSpinner from './components/LoadingSpinner.vue'

defineOptions({ name: 'App' })

// ── Estado reativo ──────────────────────────────────────────
const snackbar = ref({
  show:    false,
  message: '',
  color:   'info',
  timeout: 3000
})

const globalLoading  = ref(false)
const loadingMessage = ref('Carregando...')

// ── Métodos globais ─────────────────────────────────────────
/**
 * Exibe o snackbar de notificação.
 * @param {string} message  - Texto da mensagem
 * @param {string} color    - Cor do Vuetify (success | error | warning | info)
 * @param {number} timeout  - Duração em ms (padrão: 3000)
 */
const showSnackbar = (message, color = 'info', timeout = 3000) => {
  snackbar.value = { show: true, message, color, timeout }
}

/**
 * Controla o spinner de loading global.
 * @param {boolean} show    - Exibir ou ocultar
 * @param {string}  message - Mensagem exibida (padrão: 'Carregando...')
 */
const setLoading = (show, message = 'Carregando...') => {
  globalLoading.value  = show
  loadingMessage.value = message
}

// ── Provide/Inject — expõe para todos os filhos ─────────────
// Uso nos filhos: const showSnackbar = inject('showSnackbar')
provide('showSnackbar', showSnackbar)
provide('setLoading',   setLoading)
provide('globalLoading', globalLoading)
</script>

<style>
/* ── VARIÁVEIS GLOBAIS ───────────────────────────────────────── */
:root {
  --accent:        #2563eb;
  --accent-soft:   #dbeafe;
  --accent-strong: #1d4ed8;
  --heading:       #1e293b;
  --text:          #475569;
  --muted:         #94a3b8;
  --card-bg:       #ffffff;
  --border:        #e2e8f0;
  --radius-sm:     8px;
  --radius-md:     12px;
  --radius-lg:     16px;
  --shadow-sm:     0 2px 8px rgba(0,0,0,0.06);
  --shadow-md:     0 4px 14px rgba(0,0,0,0.08);
  --shadow-lg:     0 8px 28px rgba(0,0,0,0.12);
  --transition:    all .25s ease;
}

/* ── RESET ──────────────────────────────────────────────────── */
*, *::before, *::after {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* ── BODY ───────────────────────────────────────────────────── */
body {
  font-family: 'Poppins', 'Segoe UI', system-ui, sans-serif;
  background: linear-gradient(135deg, #f1f5ff 0%, #e0e7ff 100%);
  background-attachment: fixed;
  color: var(--text);
  min-height: 100vh;
  line-height: 1.6;
}

/* ── VUETIFY ROOT ───────────────────────────────────────────── */
.v-application {
  background: transparent !important;
  font-family: inherit !important;
}

/* ── LAYOUT PRINCIPAL ───────────────────────────────────────── */
.main-layout {
  min-height: 100vh;
  padding: 16px;
}

.page-container {
  max-width: 1400px;
  margin: auto;
}

/* ── CARDS ──────────────────────────────────────────────────── */
.v-card {
  border-radius: var(--radius-lg) !important;
  border: 1px solid rgba(0,0,0,0.04) !important;
  box-shadow: var(--shadow-md) !important;
  transition: var(--transition);
  background: var(--card-bg) !important;
}

.v-card:hover {
  transform: translateY(-3px);
  box-shadow: var(--shadow-lg) !important;
}

/* ── BOTÕES ─────────────────────────────────────────────────── */
.v-btn {
  font-weight: 600 !important;
  letter-spacing: .2px;
  text-transform: none !important;
  border-radius: var(--radius-sm) !important;
}

/* ── INPUTS ─────────────────────────────────────────────────── */
.v-field {
  border-radius: var(--radius-sm) !important;
}

/* ── TABELAS ────────────────────────────────────────────────── */
.v-table {
  border-radius: var(--radius-md);
  overflow: hidden;
}

.v-table tbody tr {
  transition: background .15s ease;
}

.v-table tbody tr:hover {
  background: #f1f5ff !important;
}

/* ── SCROLLBAR CUSTOMIZADA ──────────────────────────────────── */
::-webkit-scrollbar       { width: 6px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb {
  background: #cbd5f5;
  border-radius: 10px;
}
::-webkit-scrollbar-thumb:hover { background: #94a3ff; }
</style>